import { getPortalUrl } from './portal-auth';

const PREFIX = 'groove-affiliate-sign-in:';
const TIMEOUT = 10 * 60 * 1000;
const validId = value => /^[a-f0-9]{64}$/.test(value || '');
const portalPath = win => new URL(getPortalUrl(win.location.href)).pathname;

// Match WindowProxy, origin, portal and random attempt. The receiver validates
// the short-lived access token with the API; no refresh/ID token is transferred.
// The opener is the Groove iframe, never its cross-origin parent.
export function openEmbeddedSignIn(win, acceptToken, completed, failed) {
  const origin = win.location.origin;
  const path = portalPath(win);
  const id = Array.from(win.crypto.getRandomValues(new Uint8Array(32)))
    .map(byte => byte.toString(16).padStart(2, '0')).join('');
  const url = new URL(getPortalUrl(win.location.href, true));
  url.searchParams.set('portal_bridge', id);
  let popup;
  let interval;
  let timeout;
  let disposed = false;
  let accepting = false;
  const cleanup = () => {
    disposed = true;
    win.removeEventListener('message', receive);
    win.clearInterval(interval);
    win.clearTimeout(timeout);
  };
  const fail = message => { if (disposed) return; cleanup(); failed(message); };
  const send = type => popup.postMessage({ type: PREFIX + type, id, path }, origin);
  const receive = async event => {
    const data = event.data;
    if (disposed || accepting || event.origin !== origin || event.source !== popup ||
        !data || data.type !== PREFIX + 'session' || data.id !== id || data.path !== path ||
        typeof data.token !== 'string' || !data.token || data.token.length > 65536) return;
    accepting = true;
    try {
      const user = await acceptToken(data.token);
      if (disposed) return;
      send('accepted');
      cleanup();
      completed(user);
    } catch (_) {
      fail('We could not reconnect your sign-in. Please try again.');
    }
  };
  win.addEventListener('message', receive);
  // Keep the opener deliberately for the paired handshake. This URL can only
  // point to the current Groove portal, never a caller-supplied destination.
  try {
    popup = win.open(url.href, '_blank', 'popup,width=560,height=760');
  } catch (_) { fail('We could not open sign-in. Please try again.'); return cleanup; }
  if (!popup) { fail('Please allow the sign-in window, then try again.'); return cleanup; }
  interval = win.setInterval(() => {
    if (popup.closed && !accepting) { fail('Sign-in was closed. Please try again.'); return; }
    try { send('request'); } catch (_) { /* Still at the identity provider. */ }
  }, 500);
  timeout = win.setTimeout(() => fail('Sign-in timed out. Please try again.'), TIMEOUT);
  return cleanup;
}

export function connectSignInWindow(win, token) {
  const id = new URL(win.location.href).searchParams.get('portal_bridge');
  if (win.self !== win.top || !win.opener || !validId(id) || !token) return () => {};
  const origin = win.location.origin;
  const path = portalPath(win);
  const opener = win.opener;
  const cleanup = () => {
    win.removeEventListener('message', receive);
    win.clearTimeout(timeout);
  };
  const receive = event => {
    const data = event.data;
    if (event.origin !== origin || event.source !== opener || !data || data.id !== id || data.path !== path) return;
    if (data.type === PREFIX + 'request') {
      opener.postMessage({ type: PREFIX + 'session', id, path, token }, origin);
    } else if (data.type === PREFIX + 'accepted') {
      cleanup();
      win.close();
    }
  };
  win.addEventListener('message', receive);
  const timeout = win.setTimeout(cleanup, TIMEOUT);
  return cleanup;
}
